import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import type { CategoryConfig } from '@/utils/categoryMeasurementMapping';

export function useCategoryConfig() {
  return useQuery({
    queryKey: ['category-config'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('product_category_config')
        .select('*')
        .order('display_order', { ascending: true });

      if (error) throw error;
      return data as CategoryConfig[];
    },
  });
}

export function useUpdateCategoryConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      ...updates
    }: Partial<CategoryConfig> & { id: string }) => {
      const { data, error } = await supabase
        .from('product_category_config')
        .update(updates)
        .eq('category', id)
        .select()
        .maybeSingle();

      if (error) throw error;
      if (!data) {
        throw new Error(`Category "${id}" not found in configuration`);
      }
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['category-config'] });
      toast.success('Category configuration updated');
    },
    onError: (error) => {
      toast.error('Failed to update category configuration', {
        description: error.message,
      });
    },
  });
}

export function useCreateCategoryConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (config: Omit<CategoryConfig, 'id' | 'created_at' | 'updated_at'>) => {
      const { data, error } = await supabase
        .from('product_category_config')
        .insert(config)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['category-config'] });
      toast.success('Category configuration created');
    },
    onError: (error) => {
      toast.error('Failed to create category configuration', {
        description: error.message,
      });
    },
  });
}

export function getCategoryConfig(
  category: string,
  categoryConfigs: CategoryConfig[] | undefined
): CategoryConfig | null {
  if (!categoryConfigs) return null;
  return categoryConfigs.find(c => c.category === category) || null;
}
